import { useCallback, useContext, useEffect, useMemo, useRef, useState } from 'react';
import {
    Alert,
    Avatar,
    Box,
    Button,
    Card,
    CardContent,
    Chip,
    FormControl,
    InputLabel,
    LinearProgress,
    MenuItem,
    Select,
    Skeleton,
    Stack,
    Tab,
    Table,
    TableBody,
    TableCell,
    TableContainer,
    TableHead,
    TableRow,
    Tabs,
    Tooltip,
    Typography,
    ThemeProvider,
    useMediaQuery,
} from '@mui/material';
import { alpha, useTheme } from '@mui/material/styles';
import ReactApexChart from 'react-apexcharts';
import {
    FaArrowUpRightFromSquare,
    FaBullseye,
    FaComment,
    FaEye,
    FaHeart,
    FaLinkedin,
    FaShareNodes,
    FaUsers,
} from 'react-icons/fa6';
import { AppVarsContext } from '../../context/AppVarsContext';
import Comments from '../comments';
import ReportPerformanceOverview, { ReportSectionIntro } from '../shared/ReportPerformanceOverview';
import { createReportSurfaceTheme, getReportPageSx, getReportSurfaceSx, getAnalyticsReportTabsSx, reportColors, styleReportChart } from '../shared/report-design';
import { resolveShowCost } from '../../system/report-visibility';

const LINKEDIN_BLUE = '#0A66C2';
const LINKEDIN_SKY = reportColors.categories[6];
const SIGNAL_GREEN = reportColors.conversions;
const SIGNAL_AMBER = reportColors.categories[3];

const toNumber = (value) => {
    const number = Number(value);
    return Number.isFinite(number) ? number : 0;
};
const formatNumber = (value) => new Intl.NumberFormat('en-US', { maximumFractionDigits: 0 }).format(toNumber(value));
const formatCompact = (value) => new Intl.NumberFormat('en-US', { notation: 'compact', maximumFractionDigits: 1 }).format(toNumber(value));
const formatCurrency = (value) => new Intl.NumberFormat('en-US', {
    style: 'currency',
    currency: 'USD',
    maximumFractionDigits: toNumber(value) >= 1000 ? 0 : 2,
}).format(toNumber(value));
const formatPercent = (value, digits = 1) => `${toNumber(value).toFixed(digits)}%`;
const formatDate = (value) => {
    if (!value) return '';
    const date = typeof value === 'number' ? new Date(value) : new Date(`${value}T00:00:00Z`);
    if (Number.isNaN(date.getTime())) return '';
    return new Intl.DateTimeFormat('en-US', { month: 'short', day: 'numeric', year: 'numeric', timeZone: 'UTC' }).format(date);
};

const Section = ({ title, subtitle, action, children, sx = {} }) => {
    const theme = useTheme();
    return (
        <Card sx={{
            ...getReportSurfaceSx(theme),
            ...sx,
        }}>
            <Box sx={{ p: { xs: 1.75, md: 2.25 }, pb: 1.25, display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', gap: 2 }}>
                <Box>
                    <Typography variant="h6" sx={{ fontWeight: 850, letterSpacing: '-0.025em' }}>{title}</Typography>
                    {subtitle ? <Typography variant="body2" color="text.secondary" sx={{ mt: 0.35 }}>{subtitle}</Typography> : null}
                </Box>
                {action}
            </Box>
            <Box sx={{ px: { xs: 1.25, md: 2 }, pb: { xs: 1.5, md: 2 } }}>{children}</Box>
        </Card>
    );
};

const EmptyState = ({ title, description }) => (
    <Box sx={{ minHeight: 220, display: 'grid', placeItems: 'center', textAlign: 'center', px: 3 }}>
        <Box sx={{ maxWidth: 440 }}>
            <FaUsers size={28} color={LINKEDIN_SKY} aria-hidden="true" />
            <Typography sx={{ mt: 1, fontWeight: 800 }}>{title}</Typography>
            <Typography variant="body2" color="text.secondary" sx={{ mt: 0.5 }}>{description}</Typography>
        </Box>
    </Box>
);

const RankedBar = ({ rows, valueKey = 'impressions', valueLabel = 'Impressions', color = LINKEDIN_BLUE }) => {
    const theme = useTheme();
    const safeRows = useMemo(() => (Array.isArray(rows) ? rows.slice(0, 8) : []), [rows]);
    const options = useMemo(() => ({
        chart: { toolbar: { show: false }, fontFamily: theme.typography.fontFamily },
        colors: [color],
        plotOptions: { bar: { horizontal: true, borderRadius: 5, barHeight: '62%' } },
        dataLabels: { enabled: false },
        grid: { borderColor: alpha(theme.palette.text.primary, 0.08), xaxis: { lines: { show: true } }, yaxis: { lines: { show: false } } },
        xaxis: {
            categories: safeRows.map((row) => row.label),
            labels: { formatter: formatCompact, style: { colors: theme.palette.text.secondary } },
        },
        yaxis: { labels: { maxWidth: 150, style: { colors: theme.palette.text.primary, fontWeight: 600 } } },
        tooltip: { theme: theme.palette.mode, y: { formatter: (value) => `${formatNumber(value)} ${valueLabel.toLowerCase()}` } },
    }), [color, safeRows, theme, valueLabel]);

    if (!safeRows.length) {
        return <EmptyState title="No audience segment available" description="LinkedIn hides small audience groups and requires the appropriate reporting permission." />;
    }
    return <ReactApexChart type="bar" height={Math.max(250, safeRows.length * 42)} options={styleReportChart(theme, { ...options, chart: { ...options.chart, type: 'bar' } })} series={[{ name: valueLabel, data: safeRows.map((row) => toNumber(row[valueKey])) }]} />;
};

const EngagementStat = ({ icon, label, value }) => (
    <Tooltip title={label}>
        <Stack direction="row" spacing={0.55} alignItems="center" aria-label={`${label}: ${formatNumber(value)}`}>
            {icon}
            <Typography variant="caption" sx={{ fontWeight: 750, fontVariantNumeric: 'tabular-nums' }}>{formatCompact(value)}</Typography>
        </Stack>
    </Tooltip>
);

const PostCard = ({ post, compact = false }) => {
    const theme = useTheme();
    return (
        <Card sx={{
            height: '100%',
            display: 'flex',
            flexDirection: 'column',
            borderRadius: 3.5,
            overflow: 'hidden',
            border: `1px solid ${alpha(LINKEDIN_BLUE, 0.13)}`,
            background: alpha(theme.palette.background.paper, 0.88),
            boxShadow: 'none',
        }}>
            {post.mediaUrl ? (
                <Box component="img" src={post.mediaUrl} alt={`Post image from ${post.authorName || 'LinkedIn'}`} loading="lazy" sx={{ width: '100%', aspectRatio: compact ? '16 / 7' : '16 / 9', maxHeight: compact ? 140 : undefined, objectFit: 'cover', bgcolor: alpha(LINKEDIN_BLUE, 0.06) }} />
            ) : (
                <Box sx={{ aspectRatio: compact ? '16 / 7' : '16 / 6', maxHeight: compact ? 140 : undefined, display: 'grid', placeItems: 'center', background: theme.palette.background.default }}>
                    <FaLinkedin size={34} color={LINKEDIN_BLUE} aria-hidden="true" />
                </Box>
            )}
            <CardContent sx={{ p: 1.75, '&:last-child': { pb: 1.75 }, display: 'flex', flexDirection: 'column', flex: 1, gap: 1.35 }}>
                {compact && <Typography variant="caption" color="text.secondary" fontWeight={700}>Best content signal</Typography>}
                <Stack direction="row" spacing={1} alignItems="center" flexWrap="wrap" useFlexGap>
                    <Avatar src={post.authorImage || ''} alt="" sx={{ width: 34, height: 34, bgcolor: LINKEDIN_BLUE }}>{(post.authorName || 'L')[0]}</Avatar>
                    <Box sx={{ minWidth: 0, flex: 1 }}>
                        <Typography variant="body2" noWrap sx={{ fontWeight: 800 }}>{post.authorName || 'LinkedIn'}</Typography>
                        <Typography variant="caption" color="text.secondary">{formatDate(post.publishedAt)}</Typography>
                    </Box>
                    <Chip size="small" label={post.source === 'sponsored' ? 'Sponsored' : post.source === 'organic' ? 'Organic · lifetime' : 'Published'} sx={{ fontWeight: 750, bgcolor: alpha(post.source === 'sponsored' ? SIGNAL_AMBER : LINKEDIN_BLUE, 0.1) }} />
                </Stack>
                <Typography variant="body2" sx={{
                    whiteSpace: compact ? 'normal' : 'pre-wrap',
                    display: '-webkit-box',
                    WebkitBoxOrient: 'vertical',
                    WebkitLineClamp: compact ? 2 : 4,
                    overflow: 'hidden',
                    lineHeight: 1.55,
                    minHeight: compact ? '3.1em' : '6.2em',
                    overflowWrap: 'anywhere',
                }}>
                    {post.text}
                </Typography>
                <Stack direction="row" spacing={1.5} alignItems="center" flexWrap="wrap" useFlexGap sx={{ color: 'text.secondary' }}>
                    <EngagementStat icon={<FaEye size={13} />} label="Impressions" value={post.impressions} />
                    <EngagementStat icon={<FaHeart size={13} />} label="Reactions" value={post.reactions} />
                    <EngagementStat icon={<FaComment size={13} />} label="Comments" value={post.comments} />
                    <EngagementStat icon={<FaShareNodes size={13} />} label="Reposts" value={post.reposts} />
                </Stack>
                <Stack direction="row" justifyContent="space-between" alignItems="center" sx={{ mt: 'auto', pt: 0.5 }}>
                    <Box>
                        <Typography variant="caption" color="text.secondary">Engagement rate</Typography>
                        <Typography sx={{ fontWeight: 850 }}>{formatPercent(post.engagementRate)}</Typography>
                    </Box>
                    {post.linkedinUrl ? (
                        <Button component="a" href={post.linkedinUrl} target="_blank" rel="noreferrer" size="small" endIcon={<FaArrowUpRightFromSquare size={12} />} sx={{ borderRadius: 999, textTransform: 'none', fontWeight: 800 }}>
                            View post
                        </Button>
                    ) : null}
                </Stack>
            </CardContent>
        </Card>
    );
};

const LinkedInReport = (props) => {
    const { appVars, appLoading } = useContext(AppVarsContext);
    const brandTheme = useTheme();
    const theme = useMemo(() => createReportSurfaceTheme(brandTheme), [brandTheme]);
    const isMobile = useMediaQuery(theme.breakpoints.down('sm'));
    const [report, setReport] = useState(null);
    const [profile, setProfile] = useState(props.reportProfile || null);
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState('');
    const [tabIndex, setTabIndex] = useState(0);
    const [audienceSource, setAudienceSource] = useState('followers');
    const reportRef = useRef(null);
    const cid = sessionStorage.getItem('cid') || sessionStorage.getItem('rid') || '';
    const setParentLoading = props.setLoading;
    const showCost = resolveShowCost(props.reportProfile);

    const fetchReport = useCallback(async () => {
        if (!appVars?.api_url || !cid) return;
        setLoading(true);
        setError('');
        setParentLoading?.(true);
        try {
            const response = await fetch(`${appVars.api_url}/linkedin/report`, {
                method: 'POST',
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify({
                    cid,
                    sdate: sessionStorage.getItem('sDate'),
                    edate: sessionStorage.getItem('eDate'),
                }),
            });
            const payload = await response.json().catch(() => ({}));
            if (!response.ok) throw new Error(payload?.error || `LinkedIn report request failed (${response.status}).`);
            setReport(payload);
        } catch (requestError) {
            setReport(null);
            setError(requestError?.message || 'LinkedIn report could not be loaded.');
        } finally {
            setLoading(false);
            setParentLoading?.(false);
        }
    }, [appVars?.api_url, cid, setParentLoading]);

    useEffect(() => {
        if (!appLoading) fetchReport();
    }, [appLoading, fetchReport]);

    useEffect(() => {
        if (profile || !appVars?.api_url || !cid) return;
        fetch(`${appVars.api_url}/reportprofile/${cid}`)
            .then((response) => response.json())
            .then(setProfile)
            .catch(() => {});
    }, [appVars?.api_url, cid, profile]);

    useEffect(() => {
        const refresh = () => fetchReport();
        window.addEventListener('dc', refresh);
        return () => window.removeEventListener('dc', refresh);
    }, [fetchReport]);

    const paid = report?.paid || {};
    const paidSummary = paid.summary || {};
    const organic = report?.organic || {};
    const organicSummary = organic.summary || {};
    const posts = report?.content?.posts || [];
    const topPost = [...posts].sort((a, b) => toNumber(b.engagementRate) - toNumber(a.engagementRate) || toNumber(b.impressions) - toNumber(a.impressions))[0];
    const warnings = report?.meta?.warnings || [];
    const coverage = report?.meta?.coverage || {};
    const tabs = ['Performance', 'Content', 'Audience', 'Campaigns', 'Notes'];
    const demographics = audienceSource === 'paid' ? paid.demographics || {} : organic.followerAudience || {};
    const audienceValueKey = audienceSource === 'paid' ? 'impressions' : 'followers';
    const audienceValueLabel = audienceSource === 'paid' ? 'Impressions' : 'Followers';

    const trendOptions = useMemo(() => ({
        chart: { toolbar: { show: false }, zoom: { enabled: false }, fontFamily: theme.typography.fontFamily },
        colors: [LINKEDIN_SKY, LINKEDIN_BLUE, SIGNAL_GREEN],
        dataLabels: { enabled: false },
        stroke: { width: [2, 3, 3], curve: 'smooth' },
        grid: { borderColor: alpha(theme.palette.text.primary, 0.08) },
        xaxis: {
            type: 'datetime',
            categories: (paid.daily || []).map((row) => `${row.date}T00:00:00Z`),
            labels: { datetimeUTC: true, style: { colors: theme.palette.text.secondary } },
        },
        yaxis: [
            { labels: { formatter: formatCompact, style: { colors: theme.palette.text.secondary } }, title: { text: 'Impressions' } },
            { opposite: true, labels: { formatter: formatCompact, style: { colors: theme.palette.text.secondary } }, title: { text: 'Actions' } },
        ],
        tooltip: { shared: true, theme: theme.palette.mode, x: { format: 'MMM dd, yyyy' }, y: { formatter: formatNumber } },
        legend: { position: 'top', horizontalAlign: 'left' },
    }), [paid.daily, theme]);
    const trendSeries = useMemo(() => [
        { name: 'Impressions', type: 'line', data: (paid.daily || []).map((row) => toNumber(row.impressions)) },
        { name: 'Clicks', type: 'line', data: (paid.daily || []).map((row) => toNumber(row.clicks)) },
        { name: 'Conversions', type: 'line', data: (paid.daily || []).map((row) => toNumber(row.conversions)) },
    ], [paid.daily]);

    const contentTrend = useMemo(() => report?.content?.sponsoredDaily || [], [report?.content?.sponsoredDaily]);
    const contentTrendOptions = useMemo(() => ({
        ...trendOptions,
        colors: [LINKEDIN_BLUE, SIGNAL_AMBER, SIGNAL_GREEN],
        xaxis: { ...trendOptions.xaxis, categories: contentTrend.map((row) => `${row.date}T00:00:00Z`) },
        yaxis: [{ labels: { formatter: formatCompact }, title: { text: 'Engagements' } }],
    }), [contentTrend, trendOptions]);
    const contentTrendSeries = useMemo(() => [
        { name: 'Clicks', type: 'line', data: contentTrend.map((row) => toNumber(row.clicks)) },
        { name: 'Reactions', type: 'line', data: contentTrend.map((row) => toNumber(row.reactions)) },
        { name: 'Comments + reposts', type: 'line', data: contentTrend.map((row) => toNumber(row.comments) + toNumber(row.reposts)) },
    ], [contentTrend]);

    const surface = getReportSurfaceSx(theme);

    return (
        <ThemeProvider theme={theme}><Box ref={reportRef} sx={{ width: '100%', minWidth: 0 }}>
            <Box sx={getReportPageSx(theme)}>
                <Box sx={{ position: 'relative', display: 'grid', gap: 2 }}>
                    {loading ? <LinearProgress sx={{ borderRadius: 999 }} /> : null}
                    {error ? <Alert severity="error" action={<Button color="inherit" size="small" onClick={fetchReport}>Retry</Button>}>{error}</Alert> : null}
                    {warnings.length ? (
                        <Alert severity="warning" sx={{ ...surface, alignItems: 'flex-start' }}>
                            <Typography variant="body2" sx={{ fontWeight: 750 }}>{warnings[0]}</Typography>
                            {warnings.length > 1 ? (
                                <Box component="details" sx={{ mt: 0.75 }}>
                                    <Typography component="summary" variant="caption" sx={{ cursor: 'pointer', fontWeight: 800 }}>
                                        Show {warnings.length - 1} more coverage note{warnings.length > 2 ? 's' : ''}
                                    </Typography>
                                    <Box component="ul" sx={{ mt: 0.75, mb: 0, pl: 2.25 }}>
                                        {warnings.slice(1).map((warning) => <li key={warning}><Typography variant="caption">{warning}</Typography></li>)}
                                    </Box>
                                </Box>
                            ) : null}
                        </Alert>
                    ) : null}

                    <Box sx={{ ...surface, px: 1, py: { xs: 1, sm: 0 } }}>
                        {isMobile ? (
                            <FormControl fullWidth size="small">
                                <InputLabel id="linkedin-report-section-label">Report section</InputLabel>
                                <Select labelId="linkedin-report-section-label" label="Report section" value={tabIndex} onChange={(event) => setTabIndex(Number(event.target.value))} sx={{ borderRadius: 999 }}>
                                    {tabs.map((label, index) => <MenuItem key={label} value={index}>{label}</MenuItem>)}
                                </Select>
                            </FormControl>
                        ) : (
                            <Tabs value={tabIndex} onChange={(_, value) => setTabIndex(value)} variant="scrollable" scrollButtons="auto" sx={getAnalyticsReportTabsSx()}>
                                {tabs.map((label) => <Tab key={label} label={label} />)}
                            </Tabs>
                        )}
                    </Box>

                    {!report && loading ? (
                        <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', md: 'repeat(3, 1fr)' }, gap: 2 }}>
                            {[0, 1, 2].map((item) => <Skeleton key={item} variant="rounded" height={190} sx={{ borderRadius: 4 }} />)}
                        </Box>
                    ) : null}

                    {report && tabIndex === 0 ? (
                        <>
                            <ReportPerformanceOverview eyebrow="LinkedIn" title="From attention to action" description="Connect paid reach, website actions, and Page engagement."
                                stages={[
                                    { label: 'Reach', description: 'Paid visibility and response.', metrics: [{ label: 'Impressions', value: formatNumber(paidSummary.impressions), detail: 'Paid impressions' }, { label: 'CTR', value: formatPercent(paidSummary.ctr), detail: 'Clicks per impression' }] },
                                    { label: 'Traffic', description: 'Visits and recorded website actions.', metrics: [{ label: 'Clicks', value: formatNumber(paidSummary.clicks), detail: 'Paid clicks' }, { label: 'Conversions', value: formatNumber(paidSummary.conversions), detail: 'Attributed website actions' }] },
                                    { label: 'Outcomes', description: 'Value and efficiency of paid actions.', metrics: [{ label: 'Conversion value', value: formatCurrency(paidSummary.revenue), detail: 'Attributed conversion value' }, { label: 'Conversion rate', value: formatPercent(paidSummary.conversionRate), detail: 'Provider-reported conversion rate' }] }
                                ]}
                                supportMetrics={showCost ? [{ label: 'Ad spend', value: formatCurrency(paidSummary.spend) }, { label: 'CPC', value: formatCurrency(paidSummary.cpc) }, { label: 'ROAS', value: `${toNumber(paidSummary.roas).toFixed(2)}x` }] : []}
                                highlights={[
                                    { label: 'Best content signal', content: topPost ? <PostCard post={topPost} compact /> : undefined, title: topPost ? undefined : 'No post data', detail: topPost ? undefined : 'Content engagement will appear when available.' },
                                    { label: 'Page engagement', title: 'Organic interactions', value: coverage.organicEngagement ? formatNumber(toNumber(organicSummary.clicks) + toNumber(organicSummary.reactions) + toNumber(organicSummary.comments) + toNumber(organicSummary.reposts)) : 'Unavailable', detail: coverage.organicEngagement ? 'Clicks, reactions, comments, and reposts' : 'Connect a Page for organic totals' },
                                    { label: 'Community', title: report.meta?.publishingProfile?.name || 'Page followers', value: coverage.followerGrowth ? formatNumber(organicSummary.newFollowers) : 'Unavailable', detail: coverage.followerGrowth ? 'New paid and organic followers' : 'Available with Page analytics' }
                                ]}
                            />

                            <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', lg: 'minmax(0, 1.75fr) minmax(280px, .75fr)' }, gap: 2 }}>
                                <Section title="Performance over time" subtitle="Reach provides scale; clicks and conversions show movement toward action.">
                                    {paid.daily?.length ? <ReactApexChart type="line" height={350} options={styleReportChart(theme, { ...trendOptions, chart: { ...trendOptions.chart, type: 'line' } })} series={trendSeries} /> : <EmptyState title="No paid trend for this period" description="LinkedIn returned no campaign activity for the selected date range." />}
                                </Section>
                                <Section title="Efficiency readout" subtitle="Quick ratios for budget and conversion quality.">
                                    <Stack spacing={1.25} sx={{ py: 1 }}>
                                        {[
                                            ...(showCost ? [['Cost per conversion', formatCurrency(paidSummary.conversions ? paidSummary.spend / paidSummary.conversions : 0)]] : []),
                                            ['Conversion rate', formatPercent(paidSummary.conversionRate)],
                                            ['Revenue per conversion', formatCurrency(paidSummary.conversions ? paidSummary.revenue / paidSummary.conversions : 0)],
                                            ['Page views', formatNumber(organicSummary.pageViews)],
                                            ['Organic engagement rate', formatPercent(organicSummary.engagementRate)],
                                        ].map(([label, value]) => (
                                            <Stack key={label} direction="row" justifyContent="space-between" spacing={2} sx={{ p: 1.25, borderRadius: 2.5, bgcolor: alpha(LINKEDIN_BLUE, 0.045) }}>
                                                <Typography variant="body2" color="text.secondary">{label}</Typography>
                                                <Typography variant="body2" sx={{ fontWeight: 850, fontVariantNumeric: 'tabular-nums' }}>{value}</Typography>
                                            </Stack>
                                        ))}
                                    </Stack>
                                </Section>
                            </Box>
                        </>
                    ) : null}

                    {tabIndex !== 0 ? <ReportSectionIntro report="LinkedIn" title={tabs[tabIndex]} description={['', 'Review your content and the attention it earned.', 'Understand the professionals reached by your ads and Page.', 'Compare campaign contribution and efficiency.', 'Keep context alongside your report.'][tabIndex]} /> : null}

                    {report && tabIndex === 1 ? (
                        <>
                            <Section title="Posts earning attention" subtitle="Preview the content and compare the actions it generated." action={<Chip label={`${posts.length} posts`} sx={{ fontWeight: 800 }} />}>
                                {posts.length ? (
                                    <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', md: 'repeat(2, minmax(0, 1fr))', xl: 'repeat(3, minmax(0, 1fr))' }, gap: 2 }}>
                                        {posts.map((post) => <PostCard key={post.id} post={post} />)}
                                    </Box>
                                ) : (
                                    <EmptyState title="No previewable posts found" description="Choose a LinkedIn Page as the publishing identity or run sponsored content in the selected period. Personal-profile history requires LinkedIn's restricted read permission." />
                                )}
                            </Section>
                            <Section title="Sponsored engagement trend" subtitle="Clicks, reactions, comments, and reposts generated by sponsored posts.">
                                {contentTrend.length ? <ReactApexChart type="line" height={330} options={styleReportChart(theme, { ...contentTrendOptions, chart: { ...contentTrendOptions.chart, type: 'line' } })} series={contentTrendSeries} /> : <EmptyState title="No sponsored engagement trend" description="LinkedIn did not return share-level engagement for this period." />}
                            </Section>
                        </>
                    ) : null}

                    {report && tabIndex === 2 ? (
                        <>
                            <Card sx={{ ...surface, borderRadius: 4, p: 1 }}>
                                <Stack direction={{ xs: 'column', sm: 'row' }} alignItems={{ xs: 'stretch', sm: 'center' }} justifyContent="space-between" gap={1}>
                                    <Box sx={{ px: 1 }}>
                                        <Typography sx={{ fontWeight: 850 }}>Audience lens</Typography>
                                        <Typography variant="caption" color="text.secondary">Paid delivery describes who saw ads; followers describe the Page community.</Typography>
                                    </Box>
                                    <Tabs value={audienceSource} onChange={(_, value) => setAudienceSource(value)} sx={getAnalyticsReportTabsSx()}>
                                        <Tab value="paid" label="Paid audience" />
                                        <Tab value="followers" label="Page followers" />
                                    </Tabs>
                                </Stack>
                            </Card>
                            <Alert severity="info" sx={{ ...surface }}>
                                LinkedIn suppresses professional demographic groups with fewer than three events. Paid demographics may be delayed 12–24 hours; follower demographics represent the Page's lifetime audience.
                            </Alert>
                            <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', lg: 'repeat(2, minmax(0, 1fr))' }, gap: 2 }}>
                                {[
                                    ['Job function', 'jobFunction', LINKEDIN_BLUE],
                                    ['Seniority', 'seniority', SIGNAL_GREEN],
                                    ['Industry', 'industry', LINKEDIN_SKY],
                                    ['Company size', 'companySize', SIGNAL_AMBER],
                                    ['Country', 'country', LINKEDIN_BLUE],
                                    ['Region', 'region', SIGNAL_GREEN],
                                ].map(([title, key, color]) => (
                                    <Section key={key} title={title} subtitle={`Top segments by ${audienceValueLabel.toLowerCase()}`}>
                                        <RankedBar rows={demographics[key]} valueKey={audienceValueKey} valueLabel={audienceValueLabel} color={color} />
                                    </Section>
                                ))}
                            </Box>
                        </>
                    ) : null}

                    {report && tabIndex === 3 ? (
                        <Section title="Campaign contribution" subtitle="Compare reach, traffic, cost, and conversion value in one ranked view." action={<Chip icon={<FaBullseye />} label={`${paid.campaigns?.length || 0} campaigns`} sx={{ fontWeight: 800 }} />}>
                            {paid.campaigns?.length ? (
                                <TableContainer sx={{ borderRadius: 2.5, border: `1px solid ${alpha(LINKEDIN_BLUE, 0.1)}` }}>
                                    <Table size="small" sx={{ minWidth: 980 }}>
                                        <TableHead>
                                            <TableRow sx={{ bgcolor: alpha(LINKEDIN_BLUE, 0.08) }}>
                                                {['Campaign', 'Impressions', 'Clicks', 'CTR', ...(showCost ? ['Spend'] : []), 'Conversions', 'Conv. rate', 'Value', ...(showCost ? ['ROAS'] : [])].map((heading) => <TableCell key={heading} align={heading === 'Campaign' ? 'left' : 'right'} sx={{ py: 1.25, fontWeight: 850 }}>{heading}</TableCell>)}
                                            </TableRow>
                                        </TableHead>
                                        <TableBody>
                                            {paid.campaigns.map((campaign) => (
                                                <TableRow key={campaign.urn} hover>
                                                    <TableCell sx={{ maxWidth: 300 }}><Typography variant="body2" sx={{ fontWeight: 750 }}>{campaign.name}</Typography></TableCell>
                                                    <TableCell align="right">{formatNumber(campaign.impressions)}</TableCell>
                                                    <TableCell data-report-metric="click" align="right">{formatNumber(campaign.clicks)}</TableCell>
                                                    <TableCell data-report-metric="click" align="right">{formatPercent(campaign.ctr)}</TableCell>
                                                    {showCost ? <TableCell align="right">{formatCurrency(campaign.spend)}</TableCell> : null}
                                                    <TableCell data-report-metric="conversion" align="right" sx={{ fontWeight: 800 }}>{formatNumber(campaign.conversions)}</TableCell>
                                                    <TableCell data-report-metric="conversion" align="right">{formatPercent(campaign.conversionRate)}</TableCell>
                                                    <TableCell align="right">{formatCurrency(campaign.revenue)}</TableCell>
                                                    {showCost ? <TableCell align="right" sx={{ fontWeight: 800 }}>{toNumber(campaign.roas).toFixed(2)}x</TableCell> : null}
                                                </TableRow>
                                            ))}
                                        </TableBody>
                                    </Table>
                                </TableContainer>
                            ) : <EmptyState title="No campaigns found" description="LinkedIn returned no campaign rows for the selected period." />}
                        </Section>
                    ) : null}

                    {tabIndex === 4 ? (
                        <Card sx={{ ...surface, borderRadius: 4, overflow: 'hidden' }}>
                            <Comments
                                eDate={sessionStorage.getItem('eDate')}
                                sDate={sessionStorage.getItem('sDate')}
                                color={theme.palette.primary.main}
                                admintoken={sessionStorage.getItem('_buta')}
                                view="a7edd5c0-7a77-49ed-86ad-6fe3066a0f59"
                                type="linkedin"
                                setLoading={props.setLoading}
                            />
                        </Card>
                    ) : null}
                </Box>
            </Box>
        </Box></ThemeProvider>
    );
};

export default LinkedInReport;
